import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CTable, CTableHead, CTableRow, CTableHeaderCell,
  CTableBody, CTableDataCell, CFormInput, CButton, CSpinner, CPagination, CPaginationItem,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSearch, cilPlus } from '@coreui/icons';
import api from '../../services/api';
import { downloadPdf } from '../../services/financeDownload';

const PAGE_SIZE = 30;

export default function InvoicesPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/finance/invoices', { params: { page, search: q || undefined } })
      .then((r) => {
        if (cancelled) return;
        setRows(r.data.invoices || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, q]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function doSearch() {
    setPage(1);
    setQ(search.trim());
  }

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <h5 className="mb-0">Invoices</h5>
        <CButton color="success" size="sm" component={Link} to="/finance/invoice/new">
          <CIcon icon={cilPlus} className="me-1" /> Generate invoice
        </CButton>
      </div>
      <CCard className="border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
        <CCardHeader className="bg-white d-flex flex-wrap gap-2 align-items-center py-3">
          <CFormInput
            placeholder="Search invoice no, job code, job ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 280 }}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), doSearch())}
          />
          <CButton color="success" variant="outline" size="sm" onClick={doSearch}>
            <CIcon icon={cilSearch} className="me-1" /> Search
          </CButton>
        </CCardHeader>
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : (
            <CTable hover responsive className="mb-0" style={{ fontSize: 14 }}>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>Invoice</CTableHeaderCell>
                  <CTableHeaderCell>Job</CTableHeaderCell>
                  <CTableHeaderCell>Created</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Total</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Paid</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Balance</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {rows.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={7} className="text-center text-muted py-4">No invoices.</CTableDataCell>
                  </CTableRow>
                )}
                {rows.map((inv) => (
                  <CTableRow key={inv.id}>
                    <CTableDataCell>
                      <Link to={`/finance/invoices/${inv.id}`}>{inv.invoice_no}</Link>
                    </CTableDataCell>
                    <CTableDataCell>{inv.jobcode} <span className="text-muted">(#{inv.job_jid})</span></CTableDataCell>
                    <CTableDataCell>{String(inv.created_at || '').replace('T', ' ').slice(0, 16)}</CTableDataCell>
                    <CTableDataCell className="text-end">RM {Number(inv.total).toFixed(2)}</CTableDataCell>
                    <CTableDataCell className="text-end">RM {Number(inv.amount_paid).toFixed(2)}</CTableDataCell>
                    <CTableDataCell className="text-end">
                      {Number(inv.balance_due) > 0.004 ? (
                        <span className="text-warning fw-semibold">RM {Number(inv.balance_due).toFixed(2)}</span>
                      ) : (
                        <span className="text-success">—</span>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-end text-nowrap">
                      <CButton component={Link} to={`/finance/invoices/${inv.id}`} color="link" size="sm" className="p-0 me-2">View</CButton>
                      <CButton color="link" size="sm" className="p-0" onClick={() => downloadPdf(`/finance/invoices/${inv.id}/pdf`, `${inv.invoice_no}.pdf`).catch(() => {})}>PDF</CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>

      {pages > 1 && (
        <CPagination align="center">
          <CPaginationItem disabled={page <= 1} style={{ cursor: page > 1 ? 'pointer' : 'default' }} onClick={() => page > 1 && setPage(page - 1)}>Prev</CPaginationItem>
          <CPaginationItem active>{page}</CPaginationItem>
          <CPaginationItem disabled={page >= pages} style={{ cursor: page < pages ? 'pointer' : 'default' }} onClick={() => page < pages && setPage(page + 1)}>Next</CPaginationItem>
        </CPagination>
      )}
    </>
  );
}
